import React, {
	useRef,
	useState,
	useLayoutEffect,
	useEffect,
	useCallback,
	ReactNode,
	RefObject,
} from 'react'
import { MoreVert } from '@mui/icons-material'
import { useClickOutside } from '@mantine/hooks'
import { ClassNameValue } from 'tailwind-merge'
import cn from '../../lib/utils/cn'
import IconButton from './IconButton'

interface MenuItem {
	icon?: ReactNode
	label: string
	onClick: () => void
	visible?: boolean
	disabled?: boolean
	withDivider?: boolean
}

interface OptionsMenuButtonProps {
	items: MenuItem[]
	defaultPosition?: {
		top?: number
		left?: number | string
		right?: number
		bottom?: number
		leftOffset?: number
	}
	parentScrollRef?: RefObject<HTMLDivElement>
	className?: ClassNameValue
	buttonClassName?: ClassNameValue
	menuClassName?: ClassNameValue
	itemClassName?: ClassNameValue
	iconClassName?: ClassNameValue
	labelClassName?: ClassNameValue
	moreIconClassName?: ClassNameValue
	disabled?: boolean
	isLoading?: boolean
}

const MENU_WIDTH = 160
const ITEM_HEIGHT = 20
const MENU_PADDING = 30
const MENU_VERTICAL_GAP = 6
const VIEWPORT_MARGIN = 8

const OptionsMenuButton: React.FC<OptionsMenuButtonProps> = ({
	items,
	parentScrollRef,
	defaultPosition,
	className,
	buttonClassName,
	menuClassName,
	itemClassName,
	iconClassName,
	labelClassName,
	moreIconClassName,
	disabled = false,
	isLoading = false,
}) => {
	const [opened, setOpened] = useState(false)
	const [position, setPosition] = useState<{
		top: number
		left: number
		maxHeight: number
	} | null>(null)
	const buttonRef = useRef<HTMLButtonElement>(null)
	const wrapperRef = useClickOutside(() => setOpened(false))
	const menuRef = useRef<HTMLDivElement>(null)

	const visibleItemsCount = items.filter((item) => item.visible !== false).length

	const updatePosition = useCallback(() => {
		if (!buttonRef.current) return

		const rect = buttonRef.current.getBoundingClientRect()
		const viewportHeight = window.innerHeight
		const viewportWidth = window.innerWidth
		const maxHeight = Math.max(viewportHeight - VIEWPORT_MARGIN * 2, ITEM_HEIGHT)
		const estimatedMenuHeight = Math.min(
			visibleItemsCount * ITEM_HEIGHT,
			maxHeight
		)
		const measuredMenuHeight = menuRef.current?.getBoundingClientRect().height
		const menuHeight = Math.min(measuredMenuHeight || estimatedMenuHeight, maxHeight)
		const preferredTop = defaultPosition?.top ?? rect.bottom + MENU_VERTICAL_GAP
		const overflowBottom =
			preferredTop + menuHeight - (viewportHeight - VIEWPORT_MARGIN)
		const top = Math.max(
			VIEWPORT_MARGIN,
			overflowBottom > 0 ? preferredTop - overflowBottom : preferredTop
		)

		let left = rect.left - (defaultPosition?.leftOffset ?? 175)
		if (left + MENU_WIDTH > viewportWidth - MENU_PADDING) {
			left = viewportWidth - MENU_WIDTH - MENU_PADDING
		}
		if (left < MENU_PADDING) {
			left = MENU_PADDING
		}

		setPosition({ top, left, maxHeight })
	}, [defaultPosition?.leftOffset, defaultPosition?.top, visibleItemsCount])

	useLayoutEffect(() => {
		if (!opened) {
			setPosition(null)
			return
		}

		updatePosition()

		const animationFrameId = window.requestAnimationFrame(updatePosition)
		return () => window.cancelAnimationFrame(animationFrameId)
	}, [opened, updatePosition])

	useEffect(() => {
		if (!opened) return

		window.addEventListener('resize', updatePosition)
		return () => window.removeEventListener('resize', updatePosition)
	}, [opened, updatePosition])

	useEffect(() => {
		if (!parentScrollRef?.current) return

		const handleScroll = () => setOpened(false)

		parentScrollRef.current.addEventListener('scroll', handleScroll)

		return () => {
			parentScrollRef?.current?.removeEventListener('scroll', handleScroll)
		}
	}, [opened, parentScrollRef])

	const handleItemClick = (onClick: () => void) => {
		onClick()
		setOpened(false)
	}

	useEffect(() => {
		if (isLoading) {
			setOpened(false)
		}
	}, [isLoading])

	return (
		<div ref={wrapperRef} className={cn(className)}>
			<IconButton
				ref={buttonRef}
				icon={<MoreVert className={cn('dark:fill-white', moreIconClassName)} />}
				className={cn(
					'w-7 h-7 dark:hover:bg-background-dark-hover',
					buttonClassName,
					opened && 'bg-background-light dark:bg-background-dark-hover'
				)}
				variant="transparent"
				onClick={(e) => {
					e.stopPropagation()
					setOpened((prev) => !prev)
				}}
				disabled={disabled || isLoading}
				loading={isLoading}
			/>

			{opened && position && (
				<div
					ref={menuRef}
					style={{
						top: position.top,
						left: defaultPosition?.left ?? position.left,
						right: defaultPosition?.right,
						width: MENU_WIDTH,
						maxHeight: position.maxHeight,
						zIndex: 1001,
					}}
					className={cn(
						'fixed z-[1001] bg-white dark:bg-background-dark rounded-lg shadow-lg border border-stroke-light dark:border-stroke-light-dark dark:text-text-table-dark overflow-auto',
						menuClassName
					)}
					>
						{items.map(
							(
								{
									icon,
									label,
									onClick,
									visible = true,
									disabled = false,
									withDivider = false,
								},
								index
							) =>
								visible && (
									<div
										key={index}
										className={cn(
											'group/item flex items-center gap-2 cursor-pointer px-3 py-2 hover:bg-background-light-hover dark:hover:bg-background-dark-hover',
											disabled && 'opacity-50 pointer-events-none',
											withDivider &&
												'border-b border-stroke-light dark:border-stroke-light-dark  ',
											itemClassName
										)}
										style={{ height: ITEM_HEIGHT }}
										onClick={(e) => {
											e.stopPropagation()
											if (!disabled) {
												handleItemClick(onClick)
											}
										}}
									>
										{icon && (
											<span
												className={cn(
													'w-5 h-5 text-text-secondary dark:text-text-main-dark flex items-center'
													// iconClassName
												)}
											>
												{icon}
											</span>
										)}
										<span
											className={cn(
												'text-text-main flex items-center dark:text-text-main-dark',
												labelClassName
											)}
										>
											{label}
										</span>
									</div>
								)
						)}
				</div>
			)}
		</div>
	)
}

export default OptionsMenuButton
